import React from 'react'
import { useParams } from 'react-router-dom'
import DeductionService from '../../service/DeductionService'
import Deduction, { DeductionRetrieve } from '../../domain/Deduction'
import { statusLabel } from '../../domain/Status'
import useEntity from '../../hooks/useEntity'
import { paths } from '../../routes'
import FindBox from '../ui/FindBox'
import EntityState from '../ui/EntityState'
import EntitySummary from '../ui/EntitySummary'
import StatusChanger from '../ui/StatusChanger'

const DeductionUpdateForm: React.FC = () => {
    const { id } = useParams()
    const {
        entity: deduction,
        setEntity,
        loading,
        error,
        find,
        refresh,
    } = useEntity<DeductionRetrieve>({
        kind: 'deduction',
        id,
        retrieve: (deductionId) => DeductionService.retrieve(deductionId),
        pathFor: paths.deduction.update,
        notFound: 'Deduction not found. Check the ID and try again.',
        remember: (found) => ({ label: found.remarks, accountId: found.accountId }),
    })

    const changeStatus = (status: string) =>
        DeductionService.update({ deductionId: deduction?.id as string, accountId: deduction?.accountId as string, status }).then(() =>
            setEntity((current) => current && { ...current, status })
        )

    return (
        <div className="stack">
            <FindBox kind="deduction" title="Find a Deduction" placeholder="Enter Deduction ID" value={id} loading={loading} onFind={find} />
            <EntityState id={id} loading={loading} error={error} ready={!!deduction} emptyText="Enter a Deduction ID above, or pick a recent one.">
                {deduction && (
                    <>
                        <EntitySummary
                            title="Deduction Information"
                            status={deduction.status}
                            statusLabel={statusLabel(deduction.status, Deduction.statuses(deduction))}
                            items={[
                                { label: 'Deduction ID', copy: deduction.id },
                                { label: 'Account ID', copy: deduction.accountId },
                                { label: 'Remarks', value: deduction.remarks },
                            ]}
                            refreshing={loading}
                            onRefresh={refresh}
                        />
                        <StatusChanger
                            noun="deduction"
                            current={deduction.status}
                            statuses={Deduction.statuses(deduction)}
                            journey={Deduction.journey}
                            hints={Deduction.hints}
                            onSubmit={changeStatus}
                        />
                    </>
                )}
            </EntityState>
        </div>
    )
}

export default DeductionUpdateForm
